import { Platform, StyleSheet } from 'react-native';

export const monoFont = Platform.select({
    web: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
    ios: 'Menlo',
    android: 'monospace',
    default: 'monospace',
});

const cardShadow = Platform.select({
    web: { boxShadow: '0 18px 48px rgba(34, 44, 33, 0.08)' },
    default: {
        elevation: 3,
        shadowColor: '#1b281d',
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.08,
        shadowRadius: 20,
    },
});

export const styles = StyleSheet.create({
    section: {
        paddingBottom: 96,
        paddingTop: 96,
        width: '100%',
    },
    sectionNarrow: {
        paddingBottom: 68,
        paddingTop: 68,
    },
    sectionPaper: {
        backgroundColor: '#f7f4ec',
    },
    sectionPlain: {
        backgroundColor: '#fffdf8',
    },
    sectionTint: {
        backgroundColor: '#eaf0e4',
    },
    sectionDark: {
        backgroundColor: '#17291d',
    },
    rail: {
        alignSelf: 'center',
        maxWidth: 1160,
        paddingHorizontal: 32,
        width: '100%',
    },
    railNarrow: {
        paddingHorizontal: 20,
    },
    narrowRail: {
        alignSelf: 'center',
        maxWidth: 860,
        width: '100%',
    },
    hero: {
        backgroundColor: '#f7f4ec',
        overflow: 'hidden',
        paddingBottom: 112,
        paddingTop: 86,
        position: 'relative',
        width: '100%',
    },
    heroNarrow: {
        paddingBottom: 76,
        paddingTop: 54,
    },
    heroAccentLine: {
        backgroundColor: '#d6d0c2',
        height: 1,
        left: 0,
        position: 'absolute',
        right: 0,
        top: 34,
    },
    heroAccentBlock: {
        backgroundColor: '#ea580c',
        height: 7,
        left: '12%',
        position: 'absolute',
        top: 31,
        width: 72,
    },
    heroCopy: {
        alignItems: 'center',
        alignSelf: 'center',
        maxWidth: 920,
        width: '100%',
    },
    kicker: {
        color: '#456340',
        fontSize: 13,
        fontWeight: '600',
        lineHeight: 18,
        textAlign: 'center',
        textTransform: 'uppercase',
    },
    kickerLight: {
        color: '#a9c49d',
    },
    heroTitle: {
        color: '#16251a',
        fontWeight: '600',
        marginTop: 26,
        textAlign: 'center',
    },
    heroTitleNarrow: {
        fontSize: 48,
        lineHeight: 54,
    },
    heroCursor: {
        color: '#ea580c',
    },
    heroLead: {
        color: '#425046',
        fontSize: 16,
        lineHeight: 27,
        marginTop: 28,
        maxWidth: 760,
        textAlign: 'center',
    },
    heroLeadNarrow: {
        fontSize: 16,
        lineHeight: 27,
    },
    agentLine: {
        color: '#657167',
        fontSize: 13,
        letterSpacing: 0.1,
        lineHeight: 20,
        marginTop: 26,
        textAlign: 'center',
    },
    heroActions: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
        justifyContent: 'center',
        marginTop: 34,
    },
    heroActionsNarrow: {
        alignSelf: 'stretch',
        flexDirection: 'column',
    },
    actionLink: {
        alignItems: 'center',
        borderRadius: 12,
        display: 'flex',
        fontSize: 16,
        fontWeight: '600',
        justifyContent: 'center',
        minHeight: 44,
        paddingHorizontal: 18,
        paddingVertical: 12,
        textAlign: 'center',
        textDecorationLine: 'none',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'transform 160ms ease, background-color 160ms ease, border-color 160ms ease',
        }),
    },
    actionLinkPrimary: {
        backgroundColor: '#ea580c',
        borderColor: '#ea580c',
        borderWidth: 1,
        color: '#ffffff',
    },
    actionLinkSecondary: {
        backgroundColor: 'transparent',
        borderColor: '#83907f',
        borderWidth: 1,
        color: '#29412d',
    },
    actionLinkLight: {
        backgroundColor: '#f7f4ec',
        borderColor: '#f7f4ec',
        borderWidth: 1,
        color: '#1d3824',
    },
    actionLinkOutlineLight: {
        backgroundColor: 'transparent',
        borderColor: '#79917a',
        borderWidth: 1,
        color: '#f7f4ec',
    },
    actionLinkNarrow: {
        width: '100%',
    },
    heroFootnote: {
        color: '#6c746d',
        fontSize: 12,
        lineHeight: 18,
        marginTop: 18,
        textAlign: 'center',
    },
    split: {
        alignItems: 'flex-start',
        flexDirection: 'row',
        gap: 76,
    },
    splitStacked: {
        flexDirection: 'column',
        gap: 42,
    },
    splitCopy: {
        flex: 1.2,
        minWidth: 0,
    },
    splitAside: {
        flex: 0.8,
        minWidth: 0,
        width: '100%',
    },
    sectionEyebrow: {
        color: '#477143',
        fontSize: 12,
        fontWeight: '600',
        lineHeight: 18,
        marginBottom: 16,
        textTransform: 'uppercase',
    },
    sectionEyebrowLight: {
        color: '#9fbd93',
    },
    sectionTitle: {
        color: '#17251a',
        fontSize: 40,
        fontWeight: '600',
        lineHeight: 46,
    },
    sectionTitleNarrow: {
        fontSize: 40,
        lineHeight: 46,
    },
    sectionTitleLight: {
        color: '#fffdf8',
    },
    sectionLead: {
        color: '#505c52',
        fontSize: 17,
        lineHeight: 29,
        marginTop: 22,
        maxWidth: 780,
    },
    sectionLeadLight: {
        color: '#bdc9be',
    },
    bodyText: {
        color: '#566057',
        fontSize: 17,
        lineHeight: 29,
        marginTop: 18,
    },
    projectCard: {
        backgroundColor: '#1c3021',
        borderColor: '#35543a',
        borderRadius: 14,
        borderWidth: 1,
        overflow: 'hidden',
        ...cardShadow,
    },
    projectCardHeader: {
        alignItems: 'center',
        borderBottomColor: '#35543a',
        borderBottomWidth: 1,
        flexDirection: 'row',
        gap: 8,
        paddingHorizontal: 18,
        paddingVertical: 13,
    },
    projectDot: {
        backgroundColor: '#ea580c',
        borderRadius: 999,
        height: 7,
        width: 7,
    },
    projectCardLabel: {
        color: '#a9c3a4',
        fontSize: 11,
        fontWeight: '600',
        textTransform: 'uppercase',
    },
    projectRows: {
        paddingHorizontal: 18,
        paddingVertical: 9,
    },
    projectRow: {
        borderBottomColor: '#304a35',
        borderBottomWidth: 1,
        paddingVertical: 13,
    },
    projectRowLast: {
        borderBottomWidth: 0,
    },
    projectKey: {
        color: '#84a082',
        fontSize: 10,
        fontWeight: '600',
        textTransform: 'uppercase',
    },
    projectValue: {
        color: '#f3f1e9',
        fontSize: 14,
        lineHeight: 21,
        marginTop: 5,
    },
    introBlock: {
        marginBottom: 48,
        maxWidth: 820,
    },
    stepsGrid: {
        flexDirection: 'row',
        gap: 18,
    },
    gridStacked: {
        flexDirection: 'column',
    },
    stepCard: {
        backgroundColor: '#fffdf8',
        borderColor: '#cfd8c8',
        borderRadius: 14,
        borderWidth: 1,
        flex: 1,
        paddingHorizontal: 24,
        paddingVertical: 26,
    },
    stepNumber: {
        color: '#ea580c',
        fontSize: 12,
        fontWeight: '500',
    },
    cardTitle: {
        color: '#1b2b1e',
        fontSize: 19,
        fontWeight: '600',
        lineHeight: 25,
        marginTop: 18,
    },
    cardBody: {
        color: '#59635a',
        fontSize: 15,
        lineHeight: 24,
        marginTop: 10,
    },
    sourceStrip: {
        alignItems: 'center',
        borderColor: '#bdcbb7',
        borderRadius: 10,
        borderWidth: 1,
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
        marginTop: 24,
        paddingHorizontal: 18,
        paddingVertical: 14,
    },
    sourceLabel: {
        color: '#5d6c5b',
        fontSize: 11,
        fontWeight: '600',
        marginRight: 5,
        textTransform: 'uppercase',
    },
    sourceName: {
        color: '#29452c',
        fontSize: 13,
        fontWeight: '600',
        lineHeight: 20,
    },
    sourceDivider: {
        color: '#91a18d',
        fontSize: 12,
    },
    capabilityList: {
        borderTopColor: '#d9d4c8',
        borderTopWidth: 1,
        marginTop: 48,
    },
    capabilityRow: {
        alignItems: 'flex-start',
        borderBottomColor: '#d9d4c8',
        borderBottomWidth: 1,
        flexDirection: 'row',
        gap: 22,
        paddingVertical: 24,
    },
    capabilityRowStacked: {
        gap: 14,
    },
    capabilityNumber: {
        color: '#ea580c',
        flexBasis: 42,
        flexGrow: 0,
        fontSize: 12,
        fontWeight: '500',
        lineHeight: 22,
    },
    capabilityTitle: {
        color: '#1a2a1d',
        flex: 0.8,
        fontSize: 17,
        fontWeight: '600',
        lineHeight: 24,
        minWidth: 0,
    },
    capabilityBody: {
        color: '#596359',
        flex: 1.7,
        fontSize: 15,
        lineHeight: 24,
        minWidth: 0,
    },
    caveatBar: {
        backgroundColor: '#edf1e7',
        borderLeftColor: '#6c9659',
        borderLeftWidth: 4,
        marginTop: 28,
        paddingHorizontal: 18,
        paddingVertical: 15,
    },
    caveatText: {
        color: '#405141',
        fontSize: 13,
        lineHeight: 21,
    },
});
